import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import { apiCall } from "../utils/apiService";
import { formatNumber } from "../utils/dashboardFormat";
import "./InstanceLayer.css";

const POLL_MS = 20000;

const emptySummary = {
    total: 0,
    active: 0,
    connecting: 0,
    inactive: 0,
    paused: 0,
    offline: 0,
    avg_delay: 0,
    avg_delay_label: "0s",
    healthy_percent: 0,
    healthy_label: "0% healthy",
    allowed: null,
};

const InstanceLayer = () => {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(emptySummary);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState("");
    const searchTimer = useRef(null);
    const pageRef = useRef(page);
    const limitRef = useRef(limit);
    const searchRef = useRef(search);

    pageRef.current = page;
    limitRef.current = limit;
    searchRef.current = search;

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(""), 2000);
    };

    const fetchList = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const params = new URLSearchParams({
                page: String(pageRef.current),
                limit: String(limitRef.current),
            });
            if (searchRef.current?.trim()) {
                params.set("search", searchRef.current.trim());
            }

            const response = await apiCall({
                url: `sessions/instance-list?${params.toString()}`,
                method: "GET",
            });

            const list = Array.isArray(response?.data) ? response.data : [];
            setRows(list);
            setSummary({ ...emptySummary, ...(response?.summary || {}) });
            setTotal(Number(response?.total) || list.length || 0);
            setTotalPages(Math.max(1, Number(response?.totalPages) || 1));
            if (response?.page) setPage(Number(response.page) || pageRef.current);
        } catch (error) {
            console.error("Error fetching instance list:", error);
            if (!silent) {
                setRows([]);
                setSummary(emptySummary);
                setTotal(0);
                setTotalPages(1);
            }
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        document.title = "WhatsApp Instances";
        fetchList();
    }, [fetchList, page, limit, search]);

    useEffect(() => {
        const tick = () => fetchList({ silent: true });
        const id = setInterval(tick, POLL_MS);
        const onFocus = () => tick();
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(id);
            window.removeEventListener("focus", onFocus);
        };
    }, [fetchList]);

    useEffect(() => {
        return () => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
        };
    }, []);

    const handleSearchChange = (value) => {
        setSearchInput(value);
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPage(1);
            setSearch(value);
        }, 350);
    };

    const handleGroupList = async (instanceId) => {
        try {
            const response = await apiCall({
                url: `groups?id=${instanceId}`,
                method: "GET",
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching groups:", error);
            return [];
        }
    };

    const createJSONFile = async (instanceId, name) => {
        const groups = await handleGroupList(instanceId);
        const payload = (groups || []).map((chat) => ({ id: chat.id, name: chat.name }));
        const dataStr = JSON.stringify(payload, null, 2);
        const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
        const link = document.createElement("a");
        link.setAttribute("href", dataUri);
        link.setAttribute("download", `Groups-${name || "instance"}.json`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showToast("Groups export downloaded");
    };

    const handleChangeStatus = async (row) => {
        const dbStatus = Number(row.db_status ?? row.status) === 1 ? 1 : 0;
        const next = dbStatus === 1 ? 0 : 1;
        try {
            const response = await apiCall({
                url: `sessions/update-instance-status`,
                method: "POST",
                body: { status: next, client_id: row.client_id },
            });
            if (response.success) {
                showToast(next === 1 ? "Instance resumed" : "Instance paused");
                fetchList({ silent: true });
            }
        } catch (error) {
            console.error("Error updating instance status:", error);
        }
    };

    const removeInstance = async (id) => {
        if (!window.confirm("Delete this instance? This cannot be undone.")) return;
        try {
            const response = await apiCall({
                url: `sessions/delete/${id}`,
                method: "DELETE",
            });
            if (response.success) {
                showToast("Instance deleted");
                fetchList({ silent: true });
            }
        } catch (error) {
            console.error("Error deleting instance:", error);
        }
    };

    const removeMessageLog = async (selected) => {
        if (!window.confirm("Clear message logs for this instance?")) return;
        try {
            const response = await apiCall({
                url: `user/del-logs`,
                method: "POST",
                body: { selected, type: "instance" },
            });
            if (response.success) {
                showToast("Logs cleared");
                fetchList({ silent: true });
            }
        } catch (error) {
            console.error("Error clearing logs:", error);
        }
    };

    const handleCopy = async (id) => {
        try {
            await navigator.clipboard.writeText(id);
            showToast("Client ID copied");
        } catch (err) {
            showToast("Failed to copy");
            console.error("Failed to copy text:", err);
        }
    };

    const startIndex = total === 0 ? 0 : (page - 1) * limit + 1;
    const endIndex = Math.min(page * limit, total);

    const pageButtons = (() => {
        const max = 5;
        if (totalPages <= max) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }
        const pages = new Set([1, totalPages, page, page - 1, page + 1].filter(
            (p) => p >= 1 && p <= totalPages
        ));
        return Array.from(pages).sort((a, b) => a - b);
    })();

    return (
        <>
            <div className="inst-welcome d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <h5>WhatsApp instances</h5>
                    <p>
                        Monitor connection status, delay, and manage sessions for your messaging
                        channels.
                    </p>
                </div>
                <Link
                    to="/scan-instance"
                    className="btn btn-success d-inline-flex align-items-center gap-2"
                >
                    <Icon icon="mdi:qrcode-scan" width={18} />
                    Scan Instance
                </Link>
            </div>

            <div className="row g-3 mb-3">
                <div className="col-6 col-lg-3">
                    <div className="card inst-kpi total">
                        <div className="inst-kpi-body">
                            <div>
                                <p className="inst-kpi-label">Total</p>
                                <h6 className="inst-kpi-value">{formatNumber(summary.total)}</h6>
                                <p className="inst-kpi-sub">
                                    Configured sessions
                                    {summary.allowed != null
                                        ? ` · ${formatNumber(summary.allowed)} allowed`
                                        : ""}
                                </p>
                            </div>
                            <div className="inst-kpi-icon">
                                <Icon icon="logos:whatsapp-icon" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-lg-3">
                    <div className="card inst-kpi active">
                        <div className="inst-kpi-body">
                            <div>
                                <p className="inst-kpi-label">Active</p>
                                <h6 className="inst-kpi-value">{formatNumber(summary.active)}</h6>
                                <p className="inst-kpi-sub">
                                    {summary.healthy_label ||
                                        `${summary.healthy_percent || 0}% healthy`}
                                </p>
                            </div>
                            <div className="inst-kpi-icon">
                                <Icon icon="mdi:check-circle-outline" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-lg-3">
                    <div className="card inst-kpi inactive">
                        <div className="inst-kpi-body">
                            <div>
                                <p className="inst-kpi-label">Inactive</p>
                                <h6 className="inst-kpi-value">{formatNumber(summary.inactive)}</h6>
                                <p className="inst-kpi-sub">
                                    Paused or offline
                                    {summary.connecting > 0
                                        ? ` · ${formatNumber(summary.connecting)} connecting`
                                        : ""}
                                </p>
                            </div>
                            <div className="inst-kpi-icon">
                                <Icon icon="mdi:pause-circle-outline" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-lg-3">
                    <div className="card inst-kpi delay">
                        <div className="inst-kpi-body">
                            <div>
                                <p className="inst-kpi-label">Avg delay</p>
                                <h6 className="inst-kpi-value">
                                    {summary.avg_delay_label || `${summary.avg_delay || 0}s`}
                                </h6>
                                <p className="inst-kpi-sub">Across all instances</p>
                            </div>
                            <div className="inst-kpi-icon">
                                <Icon icon="mdi:timer-outline" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {summary.connecting > 0 && (
                <div className="inst-connecting-chip">
                    <span className="dot" />
                    {formatNumber(summary.connecting)} connecting — waiting for WhatsApp ready
                </div>
            )}

            <div className="card inst-table-card">
                <div className="card-header">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <h5 className="inst-table-title">Instance list</h5>
                            <p className="inst-table-sub">
                                Pause, copy IDs, clear logs, export groups, or remove sessions
                            </p>
                        </div>
                        <Link
                            to="/scan-instance"
                            className="btn btn-sm btn-outline-success d-inline-flex align-items-center gap-1"
                        >
                            <Icon icon="mdi:plus" width={16} />
                            Add / Scan
                        </Link>
                    </div>
                </div>

                <div className="inst-toolbar">
                    <div className="inst-toolbar-left">
                        <select
                            value={limit}
                            onChange={(e) => {
                                setLimit(Number(e.target.value));
                                setPage(1);
                            }}
                            className="form-select"
                            style={{ width: 80 }}
                        >
                            {[10, 20, 50, 100].map((size) => (
                                <option key={size} value={size}>
                                    {size}
                                </option>
                            ))}
                        </select>
                        <span className="text-secondary-light text-sm">entries per page</span>
                    </div>
                    <div className="inst-toolbar-right">
                        <input
                            type="search"
                            className="form-control"
                            placeholder="Search name or client ID"
                            value={searchInput}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="inst-loading">Loading instances…</div>
                ) : total === 0 ? (
                    <div className="inst-empty">
                        <Icon icon="logos:whatsapp-icon" />
                        <h6>No instances yet</h6>
                        <p>Scan a QR code to connect your first WhatsApp session.</p>
                        <Link to="/scan-instance" className="btn btn-success btn-sm">
                            Scan Instance
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="inst-table-wrap">
                            <table className="inst-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Instance</th>
                                        <th>Status</th>
                                        <th>Delay</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const state = row.state || "offline";
                                        const tone = row.status_tone || "secondary";
                                        const label = row.status_label || state;
                                        const initials =
                                            row.initials ||
                                            String(row.name || "I")
                                                .trim()
                                                .split(/\s+/)
                                                .slice(0, 2)
                                                .map((p) => p[0]?.toUpperCase() || "")
                                                .join("") ||
                                            "I";
                                        const dbEnabled =
                                            Number(row.db_status ?? row.status) === 1;
                                        const delayLabel =
                                            row.delay_label || `${Number(row.delay) || 0}s`;
                                        const delayZero =
                                            Number(row.delay) === 0 || delayLabel === "0s";

                                        return (
                                            <tr key={row.id || row.client_id}>
                                                <td>
                                                    <span className="inst-id-badge">#{row.id}</span>
                                                </td>
                                                <td>
                                                    <div className="inst-cell-name">
                                                        <span className={`inst-avatar ${state}`}>
                                                            {initials}
                                                        </span>
                                                        <div>
                                                            <p className="inst-name">
                                                                {row.name || "Untitled"}
                                                            </p>
                                                            <p
                                                                className="inst-client-id"
                                                                title={row.client_id}
                                                            >
                                                                {row.client_id_preview ||
                                                                    row.client_id}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`inst-status ${tone}`}>
                                                        <span className="inst-dot" />
                                                        {label}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span
                                                        className={`inst-delay ${delayZero ? "zero" : ""}`}
                                                        title="Send delay"
                                                    >
                                                        <Icon icon="mdi:timer-outline" width={14} />
                                                        {delayLabel}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="inst-date">
                                                        <span className="inst-date-rel">
                                                            {row.created_relative || "—"}
                                                        </span>
                                                        <span className="inst-date-abs">
                                                            {row.created_formatted ||
                                                                (row.createdAt
                                                                    ? new Date(
                                                                          row.createdAt
                                                                      ).toLocaleString()
                                                                    : "—")}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="inst-actions">
                                                        <button
                                                            type="button"
                                                            title={
                                                                dbEnabled
                                                                    ? "Pause instance"
                                                                    : "Resume instance"
                                                            }
                                                            onClick={() => handleChangeStatus(row)}
                                                            className="inst-action pause"
                                                        >
                                                            <Icon
                                                                icon={
                                                                    dbEnabled
                                                                        ? "mingcute:pause-line"
                                                                        : "mingcute:play-line"
                                                                }
                                                            />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Copy client ID"
                                                            onClick={() =>
                                                                handleCopy(row.client_id)
                                                            }
                                                            className="inst-action copy"
                                                        >
                                                            <Icon icon="iconamoon:copy-light" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Clear message logs"
                                                            onClick={() =>
                                                                removeMessageLog(row.client_id)
                                                            }
                                                            className="inst-action logs"
                                                        >
                                                            <Icon icon="mdi:broom" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Download groups JSON"
                                                            onClick={() =>
                                                                createJSONFile(
                                                                    row.client_id,
                                                                    row.name
                                                                )
                                                            }
                                                            className="inst-action download"
                                                        >
                                                            <Icon icon="mingcute:arrow-down-line" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Delete instance"
                                                            onClick={() =>
                                                                removeInstance(row.client_id)
                                                            }
                                                            className="inst-action delete"
                                                        >
                                                            <Icon icon="mingcute:delete-2-line" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="inst-pagination">
                            <span>
                                Showing {startIndex} to {endIndex} of {formatNumber(total)} entries
                            </span>
                            <div className="inst-page-btns">
                                <button
                                    type="button"
                                    className="inst-page-btn"
                                    disabled={page <= 1}
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                >
                                    <Icon icon="ep:d-arrow-left" />
                                </button>
                                {pageButtons.map((p, idx) => {
                                    const prev = pageButtons[idx - 1];
                                    const showEllipsis = prev && p - prev > 1;
                                    return (
                                        <React.Fragment key={p}>
                                            {showEllipsis && (
                                                <span className="px-1 text-secondary-light">…</span>
                                            )}
                                            <button
                                                type="button"
                                                className={`inst-page-btn ${p === page ? "active" : ""}`}
                                                onClick={() => setPage(p)}
                                            >
                                                {p}
                                            </button>
                                        </React.Fragment>
                                    );
                                })}
                                <button
                                    type="button"
                                    className="inst-page-btn"
                                    disabled={page >= totalPages}
                                    onClick={() =>
                                        setPage((p) => Math.min(totalPages, p + 1))
                                    }
                                >
                                    <Icon icon="ep:d-arrow-right" />
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {toast ? <div className="inst-copy-toast">{toast}</div> : null}
        </>
    );
};

export default InstanceLayer;
